import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Warning as ConflictIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';

const MatchReportConflictsPage = () => {
  const { leagueId } = useParams();
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    let cancelled = false;
    const fetchConflicts = async () => {
      try {
        setLoading(true);
        const data = await unifiedApi.getMatchReportConflicts(leagueId);
        if (!cancelled) {
          setConflicts(data || []);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load match report conflicts.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchConflicts();
    return () => { cancelled = true; };
  }, [leagueId]);

  const formatGameDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 2: return 'Submitted';
      case 4: return 'Overridden';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 2: return 'success';
      case 4: return 'warning';
      default: return 'default';
    }
  };

  const renderComparisonDialog = () => {
    if (!selectedConflict) return null;
    const { homeTeamReport, awayTeamReport } = selectedConflict;

    return (
      <Dialog
        open={!!selectedConflict}
        onClose={() => setSelectedConflict(null)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CompareIcon color="warning" />
            <Typography variant="h6" component="span">
              Score Conflict — {selectedConflict.homeTeamName} vs {selectedConflict.awayTeamName}
            </Typography>
          </Stack>
          <IconButton onClick={() => setSelectedConflict(null)} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {formatGameDate(selectedConflict.gameDate)}
          </Typography>

          <Grid container spacing={2}>
            {/* Home team report */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {selectedConflict.homeTeamName}
                </Typography>
                {homeTeamReport ? (
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Score reported:</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {homeTeamReport.homeGoals} – {homeTeamReport.awayGoals}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Submitted by:</Typography>
                      <Typography variant="body2">{homeTeamReport.submittedByUserName}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Submitted:</Typography>
                      <Typography variant="body2">{formatDateTime(homeTeamReport.submittedUtc)}</Typography>
                    </Box>
                    {homeTeamReport.updatedUtc && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Last edited:</Typography>
                        <Typography variant="body2">{formatDateTime(homeTeamReport.updatedUtc)}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Status:</Typography>
                      <Chip
                        label={getStatusLabel(homeTeamReport.status)}
                        color={getStatusColor(homeTeamReport.status)}
                        size="small"
                        variant="outlined"
                        clickable={false}
                        onClick={() => {}}
                      />
                    </Box>
                    <Button
                      component={Link}
                      to={`/games/${selectedConflict.gameId}/teams/${homeTeamReport.submittingTeamId}/report/edit`}
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      sx={{ mt: 1 }}
                    >
                      Edit Report
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">No report submitted</Typography>
                )}
              </Paper>
            </Grid>

            {/* Away team report */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {selectedConflict.awayTeamName}
                </Typography>
                {awayTeamReport ? (
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Score reported:</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {awayTeamReport.homeGoals} – {awayTeamReport.awayGoals}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Submitted by:</Typography>
                      <Typography variant="body2">{awayTeamReport.submittedByUserName}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Submitted:</Typography>
                      <Typography variant="body2">{formatDateTime(awayTeamReport.submittedUtc)}</Typography>
                    </Box>
                    {awayTeamReport.updatedUtc && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Last edited:</Typography>
                        <Typography variant="body2">{formatDateTime(awayTeamReport.updatedUtc)}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Status:</Typography>
                      <Chip
                        label={getStatusLabel(awayTeamReport.status)}
                        color={getStatusColor(awayTeamReport.status)}
                        size="small"
                        variant="outlined"
                        clickable={false}
                        onClick={() => {}}
                      />
                    </Box>
                    <Button
                      component={Link}
                      to={`/games/${selectedConflict.gameId}/teams/${awayTeamReport.submittingTeamId}/report/edit`}
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      sx={{ mt: 1 }}
                    >
                      Edit Report
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">No report submitted</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <AdminLayout title="Match Report Conflicts" leagueId={leagueId}>
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <ConflictIcon color="warning" />
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={600}>
              Match Report Conflicts
            </Typography>
            {conflicts.length > 0 && (
              <Chip
                data-testid="conflict-count-badge"
                label={conflicts.length}
                color="error"
                size="small"
                clickable={false}
                onClick={() => {}}
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {conflicts.length === 0 && !error && (
            <Alert severity="info">
              No score conflicts found. All match reports are in agreement.
            </Alert>
          )}

          {conflicts.length > 0 && !isMobile && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Match</TableCell>
                    <TableCell align="center">Home Report</TableCell>
                    <TableCell align="center">Away Report</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conflicts.map((conflict) => (
                    <TableRow key={conflict.gameId} hover>
                      <TableCell>
                        <Typography variant="body2">{formatGameDate(conflict.gameDate)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {conflict.homeTeamName} vs {conflict.awayTeamName}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {conflict.homeTeamReport && (
                          <Stack alignItems="center" spacing={0.5}>
                            <Typography variant="body2" fontWeight={600}>
                              {conflict.homeTeamReport.homeGoals} – {conflict.homeTeamReport.awayGoals}
                            </Typography>
                            <Chip
                              label={getStatusLabel(conflict.homeTeamReport.status)}
                              color={getStatusColor(conflict.homeTeamReport.status)}
                              size="small"
                              variant="outlined"
                              clickable={false}
                              onClick={() => {}}
                            />
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {conflict.awayTeamReport && (
                          <Stack alignItems="center" spacing={0.5}>
                            <Typography variant="body2" fontWeight={600}>
                              {conflict.awayTeamReport.homeGoals} – {conflict.awayTeamReport.awayGoals}
                            </Typography>
                            <Chip
                              label={getStatusLabel(conflict.awayTeamReport.status)}
                              color={getStatusColor(conflict.awayTeamReport.status)}
                              size="small"
                              variant="outlined"
                              clickable={false}
                              onClick={() => {}}
                            />
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CompareIcon />}
                          onClick={() => setSelectedConflict(conflict)}
                          sx={{ textTransform: 'none' }}
                        >
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Mobile card view */}
          {conflicts.length > 0 && isMobile && (
            <Stack spacing={1.5}>
              {conflicts.map((conflict) => (
                <Card key={conflict.gameId} variant="outlined">
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatGameDate(conflict.gameDate)}
                    </Typography>
                    <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
                      {conflict.homeTeamName} vs {conflict.awayTeamName}
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Home says</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {conflict.homeTeamReport?.homeGoals} – {conflict.homeTeamReport?.awayGoals}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary">Away says</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {conflict.awayTeamReport?.homeGoals} – {conflict.awayTeamReport?.awayGoals}
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<CompareIcon />}
                      onClick={() => setSelectedConflict(conflict)}
                      sx={{ textTransform: 'none' }}
                    >
                      Review & Resolve
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}

          {renderComparisonDialog()}
        </>
      )}
    </AdminLayout>
  );
};

export default MatchReportConflictsPage;
